import React, { useContext, useEffect, useRef, useState } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import MatomoAsyncProvider from '../common/services/MatomoProvider';

import scrollTo from '../../lib/js/scrollTo';
import { colors, dimensions } from '../common/Theme';
import Button from '../common/elements/Button';

import { RichText } from 'prismic-reactjs';
import { fixText } from '../../lib/prismic';
import { SUPPORT_FORM_TOGGLE_ID } from '../common/elements/SupportForm';
import ContinueLink from './ContinueLink';
import SimpleImage from '../common/prismic/SimpleImage';

const bannerImageHeight = '439px';

const maxWidth = `${parseInt(dimensions.ywgSectionMaxWidth) - 1}px`;
const iconWidth = 64;

const styles = css`
  :global(body) {
    background: white;
  }

  h1 {
    font-size: 2.5rem;
  }

  h2 {
    font-size: 2.25rem;
  }

  p {
    margin-bottom: 16px;
    font-size: 1.25rem;
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  a {
    text-decoration: underline;
  }

  a:active,
  a:focus,
  a:hover {
    text-decoration: none;
  }

  .section {
    flex-direction: row;
  }

  .section--left {
    flex-direction: row-reverse;
  }

  .section-text-container {
    flex-grow: 1;
  }

  .section--left > .section-image-container {
    margin-right: 30px;
  }

  .section--right > .section-image-container {
    margin-left: 30px;
  }

  .section-image-container {
    display: flex;
    align-items: flex-end;
  }

  .section-icon::before {
    vertical-align: bottom;
    width: ${iconWidth}px;
    height: ${iconWidth}px;
    display: block;
    float: left;
    margin-top: 12px;
    content: '';
    margin-right: 12px;
    background: url('/static/images/icons/send.svg') no-repeat center
      ${colors.ywgPrimary};
    border-radius: 50%;
  }

  .section-icon--globe::before {
    background-image: url('/static/images/icons/globe.svg');
  }

  .section-icon--money::before {
    background-image: url('/static/images/icons/money.svg');
  }

  .section-icon--send::before {
    background-image: url('/static/images/icons/send.svg');
  }

  .section-icon--smiley::before {
    background-image: url('/static/images/icons/smiley.svg');
  }

  .wrapper {
    position: relative;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
  }

  .banner {
    display: flex;
    margin-top: 0px;
    min-height: ${bannerImageHeight};
    justify-content: center;
  }

  @media (min-width: ${maxWidth}) {
    .banner {
      margin-top: 40px;
    }
  }

  .box {
    display: flex;
    margin-top: 24px;
    justify-content: center;
  }

  .box-colored {
    display: flex;
    margin: 16px 0;
    padding: 15px;
    justify-content: center;
    align-items: center;
    background-color: ${colors.ywgPaleGrey};
  }

  .title-active {
    color: ${colors.ywgPinkishOrange};
  }

  .title-underline {
    text-decoration: underline;
  }

  .action-buttons {
    display: flex;
  }

  .faq {
    display: flex;
    margin-top: 124px;
    min-height: 331px;
    justify-content: center;
  }

  .faq-title {
    margin: 0;
    font-size: 3rem;
  }

  @media (max-width: 740px) {
    .section-image-container {
      display: none;
    }
  }

  @media (max-width: ${maxWidth}) {
    section {
      padding: 0 20px;
    }

    .link-inline-container {
      text-align: center;
    }
  }
  @media (min-width: ${maxWidth}) {
    .box {
      margin-top: 95px;
    }
    .section {
      padding-top: 90px;
    }
    .section-icon {
      position: relative;
    }

    .section-icon::before {
      position: absolute;
      float: none;
      top: -94px;
      background: url('/static/images/icons/send.svg') no-repeat center
        ${colors.ywgPrimary};
      border-radius: 50%;
    }
  }

  @media (min-width: ${dimensions.ywgSectionMaxWidth}) {
    h1 {
      max-width: 50%;
    }
    .wrapper {
      max-width: ${dimensions.ywgSectionMaxWidth};
    }

    .text-section {
      padding-right: 25%;
    }
    .text-section-alt {
      padding-right: 0;
    }

    .faq {
      padding-left: 50%;
    }

    .box-colored {
      margin: 20px 0;
      flex-direction: row;
    }

    .path-1::before {
      position: absolute;
      left: 30px;
      bottom: -120px;
      width: 233px;
      height: 180px;
      content: '';
      background: url('/static/images/paths/path-1.svg') no-repeat;
      z-index: -1;
    }

    .path-2 {
      padding-top: 130px;
    }

    .path-2::after {
      position: absolute;
      left: 115px;
      bottom: -427px;
      width: 380px;
      height: 443px;
      content: '';
      background: url('/static/images/paths/path-2.svg') no-repeat;
      z-index: -1;
    }

    .path-2-next {
      margin-top: 170px;
    }

    .path-2-next + .section-image-container {
      margin-right: 212px;
    }

    .path-3 {
      margin-top: 50px;
      padding-top: 130px;
    }

    .path-3::before {
      position: absolute;
      left: 32px;
      top: 6px;
      width: 514px;
      height: 198px;
      content: '';
      background: url('/static/images/paths/path-3.svg') no-repeat;
      z-index: -1;
    }

    .path-4 {
      margin-top: 50px;
      margin-left: 30px;
      padding-top: 120px;
    }

    .path-4::before {
      position: absolute;
      left: 120px;
      top: -16px;
      width: 357px;
      height: 248px;
      content: '';
      background: url('/static/images/paths/path-4.svg') no-repeat;
      z-index: -1;
    }
  }

  .box-fixed {
    position: fixed;
    margin: 0;
    top: 0;
    left: 0;
    right: 0;
    transform: translate3d(0, -130%, 0);
    transition: transform 0.3s ease;
    z-index: 10;
  }

  .box-fixed--active {
    transform: translate3d(0, 0, 0);
  }
`;

const sectionImageStyles = css.resolve`
  @media (max-width: ${maxWidth}) {
    .section-image-container__image {
      max-width: 100%;
      height: auto;
    }
  }
`;

const bannerImageStyles = css.resolve`
  .banner-img {
    position: absolute;
    top: 0;
    right: 0;
    z-index: -1;
  }
`;

const secondaryButtonStyles = css.resolve`
  .secondary-button {
    margin-left: 24px;
  }
  @media (max-width: ${maxWidth}) {
    .secondary-button {
      display: none;
    }
  }
`;

const subscribeButtonStyles = css.resolve`
  .subscribe-button {
    margin: 15px 0 0;
  }

  .subscribe-button-fixed {
    margin: 0;
  }

  @media (min-width: ${dimensions.ywgSectionMaxWidth}) {
    .subscribe-button {
      margin: 0 0 0 32px;
    }
  }
`;

const faqLinkStyles = css.resolve`
  .link-inline {
    display: inline-block;
  }
`;

const smoothScroll = e => {
  e.preventDefault();
  const id = e.currentTarget.href.split('#')[1];
  const top = document.querySelector(`#${id}`).offsetTop;
  scrollTo({ top });
};

const Section = ({
  titleNormal,
  titleSpecial,
  text,
  params,
  image,
  position
}) => {
  return (
    <>
      <section
        id={params.id}
        className={`wrapper section section--${position}`}
      >
        <div className={classnames(params.className, 'section-text-container')}>
          <h2 className={`section-icon section-icon--${params.icon}`}>
            {titleNormal} <span className="title-active">{titleSpecial}</span>
          </h2>
          {text
            ? text.map((paragraph, pIndex) => (
                <p key={`${params.id}-p-${pIndex}`}>{paragraph.text}</p>
              ))
            : null}
          {params.next}
        </div>
        {image ? (
          <div className="section-image-container">
            <SimpleImage
              image={image}
              className={classnames(
                'section-image-container__image',
                sectionImageStyles.className
              )}
            />
          </div>
        ) : null}
      </section>
      <style jsx>{styles}</style>
      {sectionImageStyles.styles}
    </>
  );
};

Section.propTypes = {
  titleNormal: PropTypes.string.isRequired,
  titleSpecial: PropTypes.string.isRequired,
  text: PropTypes.array.isRequired,
  params: PropTypes.object.isRequired,
  image: PropTypes.object.isRequired,
  position: PropTypes.oneOf(['left', 'right']).isRequired
};

const simpleInsurance = 'simple';
const adaptedInsurance = 'adaptee';
const goodPriceInsurance = 'bon-prix';
const subscribeHref = '/assurance/simulation';
const firstSectionParams = {
  id: 'flexible',
  className: 'path-2',
  icon: 'send',
  next: <ContinueLink href={`#${simpleInsurance}`} onClick={smoothScroll} />
};
const sectionParams = [
  {
    id: simpleInsurance,
    className: 'text-section text-section-alt path-2-next',
    icon: 'smiley',
    next: <ContinueLink href={`#${adaptedInsurance}`} onClick={smoothScroll} />
  },
  {
    id: adaptedInsurance,
    className: 'path-3',
    icon: 'section-icon text-section section-icon--globe',
    next: (
      <div className="link-inline-container">
        <Button
          className={classnames('link-inline', faqLinkStyles.className)}
          href="/assurance/garanties"
          secondary
        >
          Découvrir nos couvertures
        </Button>
      </div>
    )
  },
  {
    id: goodPriceInsurance,
    className: 'path-4 text-section text-section-alt',
    icon: 'money'
  }
];

const Assurance = ({ content }) => {
  const bannerImage = content.banner_image;
  const tracking = useContext(MatomoAsyncProvider.Context);
  const supportButtonClicked = () => {
    tracking.trackEvent('Support', 'Click', 'Insurance page button');
  };
  const c2aLimitRef = useRef(null);
  const [c2aFixed, setC2aFixed] = useState(false);
  const observer = useRef(null);

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      observer.current = new IntersectionObserver(
        entries => {
          if (c2aLimitRef.current === null) {
            return false;
          }
          entries.forEach(entry => {
            if (entry.intersectionRatio === 0) {
              setC2aFixed(window.scrollY > c2aLimitRef.current.offsetTop);
            }
            if (
              c2aFixed === true &&
              window.scrollY < c2aLimitRef.current.offsetTop
            ) {
              setC2aFixed(false);
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      const { current: currentObserver } = observer;
      if (c2aLimitRef.current) {
        currentObserver.observe(c2aLimitRef.current);
      }
      return () => {
        currentObserver.disconnect();
      };
    }
  }, [c2aFixed]);
  let sectionIndex = 1;
  const faqStyles = css.resolve`
    .faq {
      background: url('${content.faq_image.url}') no-repeat;
    }
  `;
  return (
    <>
      <main>
        <svg xmlns="http://www.w3.org/2000/svg" hidden>
          <path
            id="svg-arrow-right"
            fillRule="evenodd"
            d="M18 8a.993.993 0 0 0-.23-.579L11.223.263A.804.804 0 0 0 10.067.23c-.319.295-.326.87-.032 1.191l5.254 5.737H.82A.83.83 0 0 0 0 8a.83.83 0 0 0 .818.842H15.29l-5.254 5.737c-.294.32-.274.882.032 1.19a.803.803 0 0 0 1.157-.032l6.545-7.158A.76.76 0 0 0 18 8"
          />
        </svg>
        <section className="wrapper banner path-1">
          <h1>
            <div className="title-active">{content.title_1}</div>{' '}
            <div className="title-underline">{content.title_2}</div>
            {content.title_3}
          </h1>
          <div className="action-buttons">
            <Button shadowed href={subscribeHref}>
              {content.banner_button1_text}
            </Button>
            <Button
              className={classnames(
                'secondary-button',
                secondaryButtonStyles.className
              )}
              href={`#${firstSectionParams.id}`}
              onClick={smoothScroll}
              secondary
            >
              {content.banner_button2_text}
            </Button>
          </div>
          <SimpleImage
            eager
            image={bannerImage}
            className={classnames('banner-img', bannerImageStyles.className)}
          />
        </section>
        <Section
          params={firstSectionParams}
          titleNormal={content.section1_title_normal}
          titleSpecial={content.section1_title_special}
          text={content.section1_text}
          image={content.section1_image}
          position="right"
        />
        <div className="box">
          <a href={content.details_link.url}>{fixText(content.details)}</a>
        </div>
        <section ref={c2aLimitRef} className="box-colored">
          <p>{fixText(RichText.asText(content.subscribe_text))}</p>
          <Button
            shadowed
            className={classnames(
              'subscribe-button',
              subscribeButtonStyles.className
            )}
            href={content.subscribe_link.url}
          >
            {fixText(content.subscribe_link_text)}
          </Button>
        </section>
        {content.section
          ? content.section.map((section, i) => {
              const params = sectionParams[i];
              ++sectionIndex;
              return (
                <Section
                  key={`insurance-section-${params.id}`}
                  params={params}
                  titleNormal={section.title_normal}
                  titleSpecial={section.title_special}
                  text={section.text}
                  image={content[`section${sectionIndex}_image`]}
                  position={sectionIndex % 2 === 0 ? 'left' : 'right'}
                />
              );
            })
          : null}
        <section className={classnames('faq', faqStyles.className)}>
          <h2 className="faq-title">
            {content.faq_title_normal}{' '}
            <span className="title-active">{content.faq_title_special}</span>
          </h2>
          <div className="link-inline-container">
            <Button
              shadowed
              className={classnames('link-inline', faqLinkStyles.className)}
              htmlFor={SUPPORT_FORM_TOGGLE_ID}
              onClick={supportButtonClicked}
            >
              {content.faq_link_text}
            </Button>
          </div>
        </section>
        <section
          className={classnames('box-colored', 'box-fixed', {
            'box-fixed--active': c2aFixed
          })}
        >
          <Button
            shadowed
            className={classnames(
              'subscribe-button-fixed',
              subscribeButtonStyles.className
            )}
            href={content.subscribe_link.url}
          >
            {fixText(content.subscribe_link_text)}
          </Button>
        </section>
      </main>
      <style jsx>{styles}</style>
      {subscribeButtonStyles.styles}
      {bannerImageStyles.styles}
      {faqLinkStyles.styles}
      {faqStyles.styles}
      {secondaryButtonStyles.styles}
    </>
  );
};

Assurance.propTypes = {
  content: PropTypes.object.isRequired
};

export default Assurance;
